import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import ChevronDownIcon from '../icons/ChevronDownIcon';

import { Box } from '../box/box';
import { type DropdownItems } from '../dropdown/dropdown-shared';
import { getSharedProps } from '../dropdown/utilities';
import type { FieldContainerBaseProps } from '../field-container/field-container';
import { FieldContainer } from '../field-container/field-container';
import { getTextStyles } from '../text/text-styles';

import * as styles from './dropdown-field.css';

export type { DropdownItems };

export interface DropdownFieldProps<Value = string>
  extends FieldContainerBaseProps {
  defaultValue?: Value;
  name: string;
  id: string;
  placeholder?: string;
  items: DropdownItems<Value>;
  onBlur?: () => void;
  onChange?: (value: Value) => void;
  'data-testid'?: string;
  'aria-describedby'?: string;
  size?: 'medium' | 'large';
  /**
   * Override of logic to show outline error styling on dropdown without validation messages
   */
  showVisualErrorStyles?: boolean;
}

export const DropdownField = <
  // TODO what would we need to do to handle the readonly string[] type here? This is a Conform type…
  Value extends string | number | readonly string[] = string,
>({
  disabled = false,
  items,
  placeholder,
  onBlur,
  onChange,
  label,
  helpText,
  defaultValue,
  'aria-invalid': invalid,
  'aria-describedby': errorMessageId,
  name,
  id,
  size = 'medium',
  showVisualErrorStyles = false,
  showField = true,
  errors,
  readOnly = false,
}: DropdownFieldProps<Value>) => {
  const { itemPadding, stateStyles, boxProps } = getSharedProps({
    size,
    disabled: disabled || readOnly,
    hasError: invalid || showVisualErrorStyles,
  });

  return showField ? (
    <FieldContainer
      htmlFor={id}
      name={name}
      label={label}
      aria-invalid={invalid}
      errorMessageId={errorMessageId}
      errors={errors}
      helpText={helpText}
      disabled={disabled}
      readOnly={readOnly}
    >
      {readOnly ? (
        // the html select is not read only, so we need to add a hidden input to submit the value
        <input type="hidden" name={name} value={defaultValue || ''} />
      ) : null}
      <Box variant="grid" className={styles.selectWrapperGrid}>
        {/**
         *  We only apply vertical padding to the text, if we added it to the
         *  chevron icons if would make the button too tall
         */}
        <Box<HTMLSelectElement>
          Component="select"
          className={classFilter([
            getTextStyles({ variant: 'input' }),
            styles.selectResets,
            stateStyles,
          ])}
          gridItem={{ rowStart: '1', columnEnd: '3', columnStart: '1' }}
          disabled={disabled || readOnly}
          {...boxProps}
          id={id}
          name={name}
          expand
          padding={itemPadding.text}
          onBlur={onBlur}
          onChange={({ currentTarget: { value } }) => {
            onChange?.(value as Value);
          }}
          aria-invalid={invalid}
          aria-errormessage={errorMessageId} // https://stackoverflow.com/questions/51554395/what-is-the-difference-between-aria-errormessage-and-aria-alert
          defaultValue={defaultValue || ''}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {items.map(({ label, value, disabled }) => (
            <option value={value} key={String(value)} disabled={disabled}>
              {label}
            </option>
          ))}
        </Box>
        <Box
          padding={itemPadding.chevron}
          alignContent={{ y: 'center' }}
          color={boxProps.color}
          gridItem={{ rowStart: '1', columnStart: '2' }}
        >
          <ChevronDownIcon />
        </Box>
      </Box>
    </FieldContainer>
  ) : null;
};
